import React from "react";
import {
  Compass,
  ClipboardCheck,
  Network,
  TrendingUp,
  CheckCircle2,
  FileCheck,
} from "lucide-react";
import { nominationSteps, evidenceChecklist } from "../data/mock";

const iconMap = { Compass, ClipboardCheck, Network, TrendingUp };

export default function NominationGuide() {
  return (
    <section id="nominate" className="relative py-24 bg-[#FAF9F5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1F5943]/10 text-[#1F5943] mb-5">
            <FileCheck className="w-4 h-4" />
            <span className="text-xs font-bold tracking-widest uppercase">Nomination Guide</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-[#0f2a20] mb-4">
            Build a compelling,
            <br />
            <span className="text-[#C4433D]">evidence-based</span> nomination.
          </h2>
          <p className="text-[#3a5148] text-lg leading-relaxed">
            Lab Directors and CSOs can step into the gap between ambition and
            action. Four steps to make your submission stand out.
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {nominationSteps.map((s) => {
            const Icon = iconMap[s.icon] || Compass;
            return (
              <div
                key={s.step}
                className="relative p-7 rounded-2xl bg-white border border-[#1F5943]/10 hover:shadow-xl transition-all duration-500 group overflow-hidden"
              >
                <div
                  className="absolute -top-6 -right-6 font-display text-8xl text-[#1F5943]/5 group-hover:text-[#1F5943]/10 transition-colors"
                  aria-hidden
                >
                  {s.step}
                </div>

                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-[#1F5943] flex items-center justify-center mb-5 group-hover:scale-110 group-hover:rotate-6 transition-transform">
                    <Icon className="w-6 h-6 text-white" strokeWidth={2} />
                  </div>
                  <div className="text-xs font-bold tracking-widest text-[#C4433D] mb-2">
                    STEP {s.step}
                  </div>
                  <h3 className="font-display text-xl text-[#0f2a20] mb-3">
                    {s.title}
                  </h3>
                  <p className="text-[#3a5148] text-sm leading-relaxed">
                    {s.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Evidence Checklist */}
        <div className="relative rounded-3xl bg-gradient-to-br from-[#1F5943] to-[#0f2a20] p-8 md:p-12 text-white overflow-hidden">
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#4ADE80]/10 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-[#F59E0B]/10 blur-3xl" />

          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 mb-5">
                <CheckCircle2 className="w-4 h-4 text-[#4ADE80]" />
                <span className="text-xs font-bold tracking-widest uppercase">
                  Evidence Checklist
                </span>
              </div>
              <h3 className="font-display text-3xl md:text-4xl leading-tight mb-4">
                Collect before you submit.
              </h3>
              <p className="text-white/70 leading-relaxed">
                Judges value honesty, ambition, and a clear roadmap. Aim for
                progress — not perfection. Bring measurable outcomes across
                every priority area.
              </p>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
              {evidenceChecklist.map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#4ADE80]/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-[#4ADE80]" />
                  </div>
                  <span className="text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
